import { ClipboardEventHandler } from 'react';
import { ActionMeta } from 'react-select';

import { getFlattenedOptions } from './getFlattenedOptions';
import { getCreatedAndSelectedOptions } from './getCreatedAndSelectedOptions';
import { BaseOption, MultiSelectProps } from '../types';
import { DEFAULT_DELIMITER } from '../constants';
import { getCleanValues } from './getCleanValues';

type PasteArgs = Required<
  Pick<MultiSelectProps, 'options' | 'canCreate' | 'optionLimit' | 'value'>
> & {
  delimiter: MultiSelectProps['pasteDelimiter'];
  onChange: (newValue: BaseOption[], action: ActionMeta<BaseOption>) => void;
};

export const getOnPaste = ({
  options,
  delimiter,
  canCreate,
  optionLimit,
  onChange,
  value,
}: PasteArgs): ClipboardEventHandler<HTMLInputElement> => {
  return (event) => {
    if (event.type === 'paste' && event.clipboardData) {
      // get text from clipboard and split by delimiter
      const capturedText = event.clipboardData.getData('Text');
      const pastedValues = capturedText.split(delimiter ?? DEFAULT_DELIMITER);
      const currentValues: string[] = value.map(({ label }) => label);
      // cleans values array by removing duplicates, empty values, and trim string values;
      const cleanValues = getCleanValues([...currentValues, ...pastedValues]);
      // get flatversion of either base options and/or nestedBaseOptions
      const flattenedOptions = getFlattenedOptions(options);
      // get separated created and selected options from options
      const [selectedOptions, createdOptions] = getCreatedAndSelectedOptions(
        flattenedOptions,
        cleanValues,
        canCreate,
      );
      // sort options in same order as clean values
      const sortedOptions = [...selectedOptions, ...createdOptions].sort(
        (a: BaseOption<string>, b: BaseOption<string>) =>
          cleanValues.indexOf(a.value) - cleanValues.indexOf(b.value),
      );
      // bulk add options. If there's an option limit, slice from option limit
      const optionsToAdd = optionLimit ? sortedOptions.slice(0, optionLimit) : sortedOptions;

      onChange(optionsToAdd, { action: 'select-option', option: undefined });
      event.preventDefault();
    }
  };
};
